import { Link } from "react-router-dom";
import { CheckCircle2, Users, ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Icon } from "../components/Icon";
import { PageHeader } from "../components/shared/PageHeader";
import { CTASection } from "../components/shared/CTASection";
import { TEAM, IMAGES } from "../data/content";

const Avatar = ({ leader }) => {
  if (leader.photo) {
    return <img src={leader.photo} alt={leader.name} className="h-full w-full object-cover object-top" />;
  }
  return (
    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand to-navy">
      <span className="font-display text-5xl font-extrabold tracking-tight text-white/90 lg:text-6xl">
        {leader.initials}
      </span>
    </div>
  );
};

export default function Team() {
  const { leader, advisors, networkNote } = TEAM;
  return (
    <div data-testid="team-page">
      <PageHeader
        testId="team-header"
        eyebrow="Our Team"
        title="Led by experienced leadership, powered by a team of specialists."
        subtitle="Global-Elect Aviation Services brings together aviation leadership and a network of subject-matter experts across safety, operations, security, AI and documentation."
        image={IMAGES.consulting}
      />

      {/* Leadership */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-aero">Leadership</span>
          </Reveal>
          <div className="mt-8 grid gap-12 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="overflow-hidden rounded-3xl border border-border bg-white shadow-sm">
                  <div className="aspect-[4/5] w-full">
                    <Avatar leader={leader} />
                  </div>
                  <div className="p-6">
                    <h2 data-testid="leader-name" className="font-display text-2xl font-extrabold tracking-tight text-navy">{leader.name}</h2>
                    <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-aero">{leader.title}</p>
                  </div>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.12}>
                <h3 className="font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl text-balance">
                  A leader focused on safety, credibility and transformation.
                </h3>
                <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground lg:text-lg">
                  {leader.bio.map((p, i) => <p key={i}>{p}</p>)}
                </div>
                <div className="mt-8">
                  <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-navy">Credentials</h4>
                  <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                    {leader.credentials.map((c) => (
                      <li key={c} className="flex items-start gap-3 text-sm font-medium text-navy">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" /> {c}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Advisory team */}
      <section className="bg-secondary py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-aero">Advisory Team</span>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
                Specialist expertise across every service line.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                Each engagement is delivered by the right specialists for the task — matched to your
                organization's needs across our five core service lines.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {advisors.map((a, i) => (
              <Reveal key={a.role} delay={i * 0.06}>
                <div data-testid={`advisor-${i}`} className="h-full rounded-2xl border border-border bg-white p-8 transition-transform hover:-translate-y-1 hover:shadow-xl">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand">
                    <Icon name={a.icon} className="h-6 w-6" />
                  </span>
                  <h3 className="mt-6 font-display text-lg font-bold text-navy">{a.role}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{a.focus}</p>
                </div>
              </Reveal>
            ))}

            <Reveal delay={advisors.length * 0.06}>
              <div className="flex h-full flex-col justify-center rounded-2xl border border-dashed border-brand/40 bg-brand/5 p-8">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand text-white">
                  <Users className="h-6 w-6" />
                </span>
                <h3 className="mt-6 font-display text-lg font-bold text-navy">A network of specialists</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{networkNote}</p>
              </div>
            </Reveal>
          </div>

          <Reveal>
            <div className="mt-12">
              <Link to="/quote" data-testid="team-quote-cta" className="inline-flex items-center gap-1.5 rounded-full bg-aero px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
                Work with our team <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
